import { createSkySceneDetails, skyApproachSceneItems } from './225-sky-scene-details.mjs';
import { createSkyPerspectiveHaze } from './235-sky-perspective-haze.mjs';
import { createSkyRenderMonitor } from './237-sky-render-monitor.mjs';
import { applySkyMassImpact } from './239-sky-mass-impact.mjs';
import { skyRenderQuality } from './179-sky-render-quality.mjs';
export { skyRenderQuality } from './179-sky-render-quality.mjs';
import { createSkyAssistedSight } from './177-sky-assisted-sight.mjs';
import { skyFlightCapabilities } from '../../../packages/domain/src/106-sky-capabilities.mjs';
import { createSkyAdaptiveQuality } from './172-sky-adaptive-quality.mjs';
import { skyTerrainSample } from '../../../packages/domain/src/87-sky-terrain.mjs';
import { skyStageArtVariant } from './134-sky-asset-manifest.mjs';
import {
  createSkyFramedPlacement,
  skyFramedView,
  skyClosePilotCamera,
} from './163-sky-framed-view.mjs';
import { skyPilotRootY } from '../../../packages/domain/src/86-sky-world-scale.mjs';
import { createSkyBriefings } from './126-sky-hologram.mjs';
import { createSkyEscorts } from './123-sky-escorts.mjs';
import { SKY_WORLDS } from '../../../packages/domain/src/75-sky-rider.mjs';
import { SKY_GUARDIAN_ENTRY_SECONDS } from '../../../packages/domain/src/76-sky-campaign.mjs';
import { skyAimSolution, skyDirectAimView } from '../../../packages/domain/src/80-sky-aim.mjs';
import { claimGameRenderer } from './38-game-materials.mjs';
import { loadSkyModels } from './113-sky-models.mjs';
import { createSkyWorld } from './114-sky-world.mjs';
import { createSkyEffects } from './115-sky-effects.mjs';
import {
  skyGuardianDefeatFrame,
  skyGuardianFlybyFrame,
  skyCinematicState,
  skyCinematicLaneY,
  skyTerminalCameraFrame,
} from './117-sky-cinematics.mjs';
import {
  SKY_CAMERA_TRACKING_LAG,
  skyCameraFrame,
  skyEncounterCameraFrame,
  createSkyHeadView,
  skyTraversalCameraFrame,
  skyTraversalPilotFrame,
  createSkyViewOrientation,
  createSkyCameraParallax,
} from './119-sky-camera.mjs';

/** Show the same non-homing endpoint used by newly emitted bolts. Equipment
 * assistance is domain-owned; the sight never independently chooses a target. */
export function skyReticlePoint(state) {
  const { x, y, z } = skyAimSolution(state);
  return { x, y, z };
}

/** One bounded WebGL2 scene and animation clock. Blender meshes, instanced props,
 * PMREM light and restrained bloom are presentation only; policy owns outcomes. */
export async function createSkyRenderer(
  canvas,
  {
    valid,
    failed,
    reduced,
    prototypeDragonURL,
    prototypeDragonRig,
    initialState,
    interiorView = 'first-person',
    interiorBlendAccepted = true,
  },
) {
  const T = await import('three');
  const [
    { RoomEnvironment },
    { EffectComposer },
    { RenderPass },
    { UnrealBloomPass },
    { OutputPass },
  ] = await Promise.all([
    import('three/addons/environments/RoomEnvironment.js'),
    import('three/addons/postprocessing/EffectComposer.js'),
    import('three/addons/postprocessing/RenderPass.js'),
    import('three/addons/postprocessing/UnrealBloomPass.js'),
    import('three/addons/postprocessing/OutputPass.js'),
  ]);
  if (!valid()) return null;
  let renderer,
    art,
    world,
    effects,
    escorts,
    pilot,
    briefings,
    composer,
    release,
    dead = false;
  const scene = new T.Scene(),
    models = new Map(),
    extra = new Set(),
    warming = new Set();
  const lifetime = new AbortController();
  const own = (value) => {
    extra.add(value);
    return value;
  };
  function dispose() {
    if (dead) return;
    dead = true;
    lifetime.abort();
    for (const group of warming) art?.releaseEnemy(group);
    warming.clear();
    canvas.removeEventListener('webglcontextlost', lost);
    briefings?.dispose();
    pilot?.dispose();
    effects?.dispose();
    escorts?.dispose();
    world?.dispose();
    for (const model of models.values()) art?.releaseEnemy?.(model);
    models.clear();
    art?.dispose();
    for (const value of extra) value.dispose();
    composer?.dispose();
    renderer?.dispose();
    release?.();
  }
  function lost(event) {
    event?.preventDefault();
    dispose();
    failed();
  }
  try {
    release = claimGameRenderer(() => lost());
    renderer = new T.WebGLRenderer({
      canvas,
      antialias: false,
      alpha: false,
      powerPreference: 'high-performance',
    });
    renderer.outputColorSpace = T.SRGBColorSpace;
    renderer.toneMapping = T.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 0.9;
    renderer.info.autoReset = false;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = T.PCFShadowMap;
    canvas.addEventListener('webglcontextlost', lost);
    art = await loadSkyModels(T, () => !dead && valid(), {
      prototypeDragonURL,
      prototypeDragonRig,
      staged: true,
      signal: lifetime.signal,
    });
    // The opening hologram must exist before its first frame; remaining combat
    // assets still prepare behind the intro, never change its duration mid-shot.
    if (
      art &&
      prototypeDragonURL &&
      initialState?.sector === 0 &&
      skyStageArtVariant(initialState) !== 'gilded-leviathan'
    )
      await art.prepareBriefing();
    if (!art || !valid() || dead) {
      dispose();
      return null;
    }
    scene.fog = new T.Fog('#aab9ae', 55, 155);
    const perspectiveHaze = own(createSkyPerspectiveHaze(T));
    world = await createSkyWorld(T, scene, art, () => !dead && valid(), reduced);
    if (!world || !valid() || dead) {
      dispose();
      return null;
    }
    const room = new RoomEnvironment(),
      pmrem = new T.PMREMGenerator(renderer);
    const environment = own(pmrem.fromScene(room, 0.06));
    scene.environment = environment.texture;
    scene.environmentIntensity = 0.5;
    room.dispose();
    pmrem.dispose();
    // Analytic sky/ground radiance: L(n) = mean + contrast * n.y.
    // Its exact SH projection needs only l=0 and l=1 coefficients. This gives
    // broad diffuse form lighting without cubemap captures or indoor probe grids.
    const probe = new T.LightProbe(new T.SphericalHarmonics3(), 0.38);
    const skyLight = new T.Color(),
      groundLight = new T.Color();
    const whiteLight = new T.Color('#ffffff');
    scene.add(probe);
    const sun = new T.DirectionalLight('#ffe1bd', 2.1);
    sun.position.set(-12, 24, 12);
    sun.castShadow = true;
    sun.shadow.mapSize.set(1024, 1024);
    Object.assign(sun.shadow.camera, {
      left: -12,
      right: 12,
      top: 14,
      bottom: -8,
      near: 1,
      far: 60,
    });
    sun.shadow.bias = -0.001;
    sun.shadow.normalBias = 0.02;
    sun.shadow.radius = 2.5;
    scene.add(sun, sun.target);
    own(sun.shadow);
    const rim = new T.DirectionalLight('#86e3ff', 0.85);
    rim.position.set(8, 8, -18);
    scene.add(rim);
    const camera = new T.PerspectiveCamera(56, 1, 0.15, 250);
    let aimState = null;
    const assistedSight = createSkyAssistedSight(T, camera, canvas);
    camera.position.set(0, 5.4, 13);
    pilot = art.rider({ reduced });
    const rider = pilot.group;
    const placeFramedPilot = createSkyFramedPlacement(T);
    const viewOrientation = createSkyViewOrientation({ reduced });
    scene.add(rider);
    briefings = createSkyBriefings(T, art);
    briefings.prepare(initialState ?? { sector: 0 });
    effects = createSkyEffects(T, scene, { reduced });
    escorts = createSkyEscorts(T, scene, art.models, { reduced });
    const { encounterEffects, sight, objectiveZones, flightCourseArt, ringGeometry, ringMaterial } =
      createSkySceneDetails(T, scene, own, reduced);
    const reticle = sight.object;
    const gl = renderer.getContext();
    const quality = skyRenderQuality(
      1,
      1,
      1,
      gl.getInternalformatParameter(gl.RENDERBUFFER, gl.RGBA16F, gl.SAMPLES),
      gl.getInternalformatParameter(gl.RENDERBUFFER, gl.DEPTH_COMPONENT24, gl.SAMPLES),
      { coarsePointer: matchMedia('(pointer: coarse)').matches },
    );
    // MSAA belongs to the composer's HDR target, not the unused default buffer.
    const target = new T.WebGLRenderTarget(1, 1, {
      type: T.HalfFloatType,
      samples: quality.samples,
      resolveDepthBuffer: false,
      storeMultisampledColorBuffer: false,
      storeMultisampledDepthBuffer: false,
    });
    composer = new EffectComposer(renderer, target);
    const renderPass = own(new RenderPass(scene, camera));
    const bloom = own(new UnrealBloomPass(new T.Vector2(512, 512), 0.28, 0.55, 1.25));
    const output = own(new OutputPass());
    composer.addPass(renderPass);
    composer.addPass(bloom);
    composer.addPass(output);
    const cameraParallax = createSkyCameraParallax({ reduced });
    const headView = createSkyHeadView();
    let width = 0,
      height = 0,
      lastSector = -1,
      lastHits = 0,
      hitAt = -10,
      lastTime = 0,
      cameraTime = null,
      cameraX = 0;
    async function compile(sceneToWarm, targetScene, warmCamera = camera) {
      perspectiveHaze.prepare(sceneToWarm);
      let cancel;
      const aborted = new Promise((resolve, reject) => {
        cancel = () => reject(new DOMException('Sky renderer closed', 'AbortError'));
        lifetime.signal.addEventListener('abort', cancel, { once: true });
      });
      try {
        await Promise.race([renderer.compileAsync(sceneToWarm, warmCamera, targetScene), aborted]);
      } finally {
        lifetime.signal.removeEventListener('abort', cancel);
      }
    }
    const phases = new Map();
    const phaseKey = (state) => `${state.sector}:${skyStageArtVariant(state) ?? ''}`;
    function stageReadiness(state) {
      if (dead)
        return { status: 'error', error: new DOMException('Sky renderer closed', 'AbortError') };
      const key = phaseKey(state);
      return phases.get(key)?.status === 'ready'
        ? { status: 'ready' }
        : phases.has(key)
          ? { status: phases.get(key).status, error: phases.get(key).error }
          : { status: 'idle' };
    }
    function prepareStage(state, { preloadOnly = false } = {}) {
      state = {
        sector: state.sector,
        leviathan: state.leviathan ? { sector: state.leviathan.sector } : null,
        boss: state.boss,
      };
      const key = phaseKey(state);
      const stage = key.endsWith(':gilded-leviathan')
        ? { ...SKY_WORLDS[state.sector], encounterId: 'gilded-leviathan' }
        : SKY_WORLDS[state.sector];
      if (dead || !valid())
        return Promise.reject(new DOMException('Sky renderer closed', 'AbortError'));
      const prior = phases.get(key);
      // A ready shader phase can outlive its evicted world assets. Re-enter
      // preparation to restore textures and compile the actual scene again.
      if (prior?.status === 'loading')
        return !preloadOnly && prior.preloadOnly
          ? prior.promise.then(() => prepareStage(state))
          : prior.promise;
      if (preloadOnly && prior?.status === 'ready') return prior.promise;
      const record = { status: 'loading', error: null, preloadOnly };
      phases.set(key, record);
      record.promise = (async () => {
        const [, landscapeTextures] = await Promise.all([
          art.prepareStage(state),
          world.prepare(stage, { preloadOnly }),
        ]);
        if (dead || !valid()) throw new DOMException('Sky renderer closed', 'AbortError');
        briefings.prepare(state);
        await briefings.warm(state, renderer, compile);
        for (const texture of landscapeTextures) renderer.initTexture(texture);
        const { keys, warmItems } = art.stageAssets(state);
        const warm = new T.Scene();
        const temporary = [];
        const effectsWarm = [
          ...effects.warmup(),
          ...world.warmup(stage),
          effects.createProjectile({ kind: 'threat', attackKind: 'fireball' }),
          effects.createProjectile({ kind: 'threat', attackKind: 'plasma' }),
        ];
        warm.add(...effectsWarm);
        try {
          // Warm actual variant programs against the active scene's lighting.
          // Texture upload is explicit; first-use vertex uploads remain driver-owned.
          for (const key of keys)
            art.models[key].scene.traverse((node) => {
              for (const material of node.material
                ? Array.isArray(node.material)
                  ? node.material
                  : [node.material]
                : [])
                for (const value of Object.values(material))
                  if (value?.isTexture) renderer.initTexture(value);
            });
          for (const item of warmItems) {
            const group = art.enemy(item);
            warm.add(group);
            temporary.push(group);
            warming.add(group);
          }
          await compile(warm, scene);
          if (dead || !valid()) throw new DOMException('Sky renderer closed', 'AbortError');
          await compile(scene);
          if (dead || !valid()) throw new DOMException('Sky renderer closed', 'AbortError');
          record.status = 'ready';
        } finally {
          warm.remove(...effectsWarm);
          for (const mesh of effectsWarm) if (mesh.isInstancedMesh) mesh.dispose();
          for (const group of temporary) {
            warm.remove(group);
            if (warming.delete(group)) art.releaseEnemy(group);
          }
        }
      })().catch((error) => {
        record.status = 'error';
        record.error = error;
        throw error;
      });
      return record.promise;
    }
    const adaptive = createSkyAdaptiveQuality();
    const monitor = own(
      createSkyRenderMonitor(canvas, renderer, adaptive, () => ({
        objectiveZones: objectiveZones.diagnostics(),
        flightCourse: flightCourseArt.diagnostics(),
        coolantStreams: encounterEffects.diagnostics().coolant,
        textureUploadLimit: art.textureUploadLimit,
      })),
    );
    function draw(state, cinematic = null) {
      const gameplay = state;
      state = skyCinematicState(state, cinematic);
      aimState = cinematic ? null : state;
      if (dead) return;
      monitor.begin(gameplay, cinematic, stageReadiness(gameplay).status === 'ready');
      const decoration = adaptive.sample({
        now: performance.now(),
        time: gameplay.time,
        active:
          !cinematic &&
          gameplay.status === 'playing' &&
          document.visibilityState !== 'hidden' &&
          stageReadiness(gameplay).status === 'ready',
      });
      renderer.info.reset();
      const rect = canvas.getBoundingClientRect(),
        w = Math.max(1, rect.width),
        h = Math.max(1, rect.height);
      if (w !== width || h !== height) {
        width = w;
        height = h;
        const { ratio, bloomScale } = skyRenderQuality(w, h, devicePixelRatio || 1, [], [], {
          coarsePointer: matchMedia('(pointer: coarse)').matches,
        });
        renderer.setPixelRatio(ratio);
        renderer.setSize(w, h, false);
        composer.setPixelRatio(ratio);
        composer.setSize(w, h);
        bloom.setSize(w * ratio * bloomScale, h * ratio * bloomScale);
        cameraTime = null;
        cameraParallax.reset();
        camera.aspect = w / h;
        camera.fov = skyCameraFrame(w / h).fov;
        camera.updateProjectionMatrix();
      }
      const hologram = cinematic?.briefing && briefings.get(state, cinematic);
      if (hologram) {
        pilot.setObserverHeadMask(false);
        hologram.draw(cinematic, w / h);
        renderPass.scene = hologram.scene;
        renderPass.camera = hologram.camera;
        bloom.strength = reduced() ? 0.04 : 0.08;
        composer.render();
        return;
      }
      renderPass.scene = scene;
      renderPass.camera = camera;
      const configuredStage = SKY_WORLDS[Math.min(SKY_WORLDS.length - 1, state.sector)];
      const stage =
        skyStageArtVariant(state) === 'gilded-leviathan'
          ? { ...configuredStage, encounterId: 'gilded-leviathan' }
          : configuredStage;
      world.draw(state, stage, w / h, decoration);
      perspectiveHaze.update(scene, renderer);
      if (lastSector !== state.sector) {
        lastSector = state.sector;
        rim.color.set(stage.accent);
        // Color.set decodes authored sRGB palettes into linear light before SH.
        skyLight.set(stage.fog).lerp(whiteLight, 0.25);
        groundLight.set(stage.ground).multiplyScalar(0.65);
        probe.sh.coefficients[0]
          .set(skyLight.r + groundLight.r, skyLight.g + groundLight.g, skyLight.b + groundLight.b)
          .multiplyScalar(Math.sqrt(Math.PI));
        probe.sh.coefficients[1]
          .set(skyLight.r - groundLight.r, skyLight.g - groundLight.g, skyLight.b - groundLight.b)
          .multiplyScalar(Math.sqrt(Math.PI / 3));
      }
      sun.position.x = state.x - 12;
      sun.target.position.x = state.x;
      const riderZ = (state.pilotZ ?? 1) - (cinematic?.riderAdvance ?? 0) * 34;
      const ground = skyTerrainSample(state.x, riderZ, state.terrainTravel ?? 0);
      rider.position.set(state.x, skyPilotRootY(state.y, ground.height), riderZ);
      const flying = state.locomotion === 'fly';
      rider.rotation.set(
        (flying ? -0.08 : -Math.atan(ground.dz)) + (cinematic?.riderPitch ?? 0),
        0,
        cinematic?.riderRoll ?? 0,
        'XYZ',
      );
      const routePose = skyTraversalPilotFrame(state);
      if (routePose) rider.rotation.set(routePose.pitch, routePose.yaw, routePose.roll, 'YXZ');
      const framed = (!cinematic || state.framedEnding) && placeFramedPilot(rider, state);
      rider.visible =
        state.status !== 'playing' ||
        state.grace <= 0 ||
        reduced() ||
        Math.floor(state.time * 10) % 2 === 0;

      if (state.time < lastTime) hitAt = -10;
      if ((state.hitsLanded ?? 0) > lastHits) hitAt = state.time;
      lastHits = state.hitsLanded ?? 0;
      lastTime = state.time;
      const impact = Math.max(0, 1 - (state.time - hitAt) / 0.16);
      let aim = skyAimSolution(state);
      const directAim = !cinematic && skyDirectAimView(state);
      if (framed || directAim)
        pilot.animate({
          ...state,
          visualAim: directAim && state.aimPoint ? state.aimPoint : aim,
          viewYaw: 0,
          viewYawResolved: true,
        });
      const primary = directAim && pilot.weaponMuzzles()?.barrels[0];
      if (primary)
        aim = skyAimSolution(state, undefined, {
          x: state.x + primary.x,
          y: state.y + primary.y,
          z: (state.pilotZ ?? 1) + primary.z,
        });
      const showSight = stage.kind !== 'bonus' && !cinematic && skyFlightCapabilities(state).aim;
      if (cinematic) {
        cameraParallax.reset();
        headView.reset();
      }
      const framedView = framed
        ? skyFramedView(state, {
            aspect: w / h,
            aim,
            observer: pilot.observerEye(),
            reduced: reduced(),
            firstPerson: interiorView === 'first-person',
            blendAccepted: interiorBlendAccepted,
          })
        : directAim
          ? skyClosePilotCamera(state, pilot.observerEye(), w / h)
          : null;
      pilot.setObserverHeadMask(Boolean(framedView?.maskWeight));
      const framing =
        framedView ??
        skyTraversalCameraFrame(
          state,
          w / h,
          headView.update(
            skyEncounterCameraFrame(cameraParallax.update(gameplay, w / h), state, w / h),
            state,
            w / h,
            aim,
          ),
          aim,
        );
      const flyby =
        cinematic || state.traversal || framed || directAim
          ? null
          : skyGuardianFlybyFrame(state.boss, SKY_GUARDIAN_ENTRY_SECONDS, reduced());
      const fov = framing.fov + (flyby?.fov ?? 0);
      const near = framing.near ?? 0.15;
      camera.up.set(framing.up?.x ?? 0, framing.up?.y ?? 1, framing.up?.z ?? 0);
      if (camera.fov !== fov || camera.near !== near) {
        camera.fov = fov;
        camera.near = near;
        camera.updateProjectionMatrix();
      }
      const elapsed =
        cameraTime === null || state.time < cameraTime ? 0 : Math.min(0.1, state.time - cameraTime);
      if (framed || directAim || cameraTime === null || state.time < cameraTime || reduced())
        cameraX = framing.x;
      else cameraX += (framing.x - cameraX) * (1 - Math.exp(-elapsed * 12));
      // Bound drag tracking lag so the rider cannot leave a narrow phone viewport.
      cameraX = Math.max(
        framing.x - SKY_CAMERA_TRACKING_LAG,
        Math.min(framing.x + SKY_CAMERA_TRACKING_LAG, cameraX),
      );
      cameraTime = state.time;
      const lookX = framing.lookX;
      camera.position.set(
        cameraX,
        framing.y + (reduced() ? 0 : impact * 0.035) + (flyby?.lift ?? 0),
        framing.z + (flyby?.pullback ?? 0),
      );
      if (cinematic) {
        const terminal = skyTerminalCameraFrame(state, cinematic, w / h);
        const blend = cinematic.settle;
        if (terminal) {
          if (terminal.up) camera.up.set(terminal.up.x, terminal.up.y, terminal.up.z);
          if (terminal.fov && camera.fov !== terminal.fov) {
            camera.fov = terminal.fov;
            camera.updateProjectionMatrix();
          }
          camera.position.set(terminal.cameraX, terminal.cameraY, terminal.cameraZ);
          camera.lookAt(terminal.lookX, terminal.lookY, terminal.lookZ);
        } else {
          camera.position.set(
            (cinematic.cameraX + (cinematic.followRider ? state.x : 0)) * (1 - blend) +
              framing.x * blend,
            (cinematic.cameraY + skyCinematicLaneY(state)) * (1 - blend) + framing.y * blend,
            (cinematic.cameraZ + (w / h < 1 ? 2 : 0)) * (1 - blend) + framing.z * blend,
          );
          camera.lookAt(
            (cinematic.followRider ? state.x : 0) * (1 - blend) + framing.lookX * blend,
            (cinematic.lookY + skyCinematicLaneY(state)) * (1 - blend) + framing.lookY * blend,
            cinematic.lookZ * (1 - blend) + framing.lookZ * blend,
          );
        }
        cameraX = framing.x;
        reticle.visible = false;
      } else {
        const defeat = skyGuardianDefeatFrame(state.boss, w / h, reduced());
        if (defeat) {
          const weight = defeat.weight;
          camera.position.x += (defeat.cameraX - camera.position.x) * weight;
          camera.position.y += (defeat.cameraY - camera.position.y) * weight;
          camera.position.z += (defeat.cameraZ - camera.position.z) * weight;
          camera.lookAt(
            lookX + (defeat.lookX - lookX) * weight,
            framing.lookY + (defeat.lookY - framing.lookY) * weight,
            framing.lookZ + (defeat.lookZ - framing.lookZ) * weight,
          );
          reticle.visible = false;
        } else camera.lookAt(lookX, framing.lookY, framing.lookZ);
      }
      if (
        (framed && !state.framedEnding) ||
        directAim ||
        state.traversal ||
        (!cinematic && !state.boss?.defeated && state.boss?.closeHead > 0)
      ) {
        camera.position.set(framing.x, framing.y, framing.z);
        camera.lookAt(framing.lookX, framing.lookY, framing.lookZ);
        cameraX = framing.x;
      }
      applySkyMassImpact(camera, state, reduced(), !cinematic);
      // Camera/parallax/flyby must resolve before projecting the exact ballistic
      // contact. The glyph billboards and retains readable CSS-pixel dimensions.
      sight.update(aim, camera, w, h, {
        impact,
        confidence: aim.confidence,
        reduced: reduced(),
        guardian: showSight ? state.boss : null,
        time: state.time,
        aimRequest: assistedSight.displayPoint(state),
        assisted: assistedSight.engaged,
        weaponsSafe: !skyFlightCapabilities(state).weapons,
      });
      reticle.visible &&= showSight;
      objectiveZones.draw(state, camera, showSight && !state.traversal, w, h);
      flightCourseArt.draw(state, !cinematic);
      encounterEffects.draw(state, !cinematic);
      const bearing = Math.atan2(camera.position.x - state.x, camera.position.z - 1);
      if (!framed && !directAim)
        pilot.animate({
          ...state,
          visualAim: aim,
          ...(state.traversal && !state.traversal.reduced
            ? { viewYaw: 0, viewYawResolved: true }
            : viewOrientation.update(state, bearing)),
        });
      effects.draw(state, rider, camera, reticle.visible ? aim : null);
      escorts.draw(state);
      const items = [
        ...skyApproachSceneItems(state),
        ...state.enemies.filter(
          (item) => !item.leviathanWall && !item.leviathanId && !item.cathedralPart,
        ),
        ...state.shots,
        ...state.threats,
        ...(state.rings ?? []),
        ...state.bursts.map((b) => ({ ...b, kind: 'burst' })),
        ...(state.boss
          ? [
              state.boss,
              ...(state.boss.authoredProps ?? []),
              ...(!state.boss.defeated ? (state.boss.prism?.illusions ?? []) : []),
            ]
          : []),
      ];
      const live = new Set();
      for (const item of items) {
        live.add(item.id);
        let model = models.get(item.id);
        const projectile = ['bolt', 'threat', 'burst'].includes(item.kind);
        const obstacle = item.kind === 'pillar' && Number.isFinite(item.hp);
        if (!model) {
          model = projectile
            ? effects.createProjectile(item)
            : item.kind === 'ring'
              ? new T.Mesh(ringGeometry, ringMaterial)
              : obstacle
                ? world.obstacle(item)
                : art.enemy(item);
          scene.add(model);
          models.set(item.id, model);
        }
        model.position.set(item.x, item.y, item.z);
        if (projectile) effects.updateProjectile(model, item, state);
        else if (item.kind === 'ring') {
          model.rotation.y = reduced() ? 0 : Math.sin(state.time) * 0.12;
        } else if (obstacle) world.animateObstacle(model, item);
        else {
          art.animateEnemy(model, item, reduced(), state);
          if (cinematic?.kind === 'guardian-escape' && item.kind === 'boss')
            model.rotation.y += (cinematic.bossRetreat ?? 0) * 1.1;
        }
      }
      for (const [id, model] of models)
        if (!live.has(id)) {
          scene.remove(model);
          world.releaseObstacle(model);
          art.releaseEnemy?.(model);
          models.delete(id);
        }
      bloom.strength = reduced() ? 0.18 : 0.28;
      world.cull?.(camera, h);
      composer.render();
      sight.drawOverlay(renderer, camera);
      monitor.end();
    }
    if (initialState) {
      await briefings.warm(initialState, renderer, compile);
      if (skyStageArtVariant(initialState) === 'gilded-leviathan') await prepareStage(initialState);
      else prepareStage(initialState).catch(() => {});
    }
    return {
      draw,
      dispose,
      prepareStage,
      stageReadiness,
      releaseBriefing: (state) => briefings.release(state),
      encounterData: () => art.encounterData(),
      diagnostics: () => (dead ? null : monitor.diagnostics()),
      weaponMuzzles: () => pilot.weaponMuzzles(),
      aimPoint: (ndc, options) => assistedSight.request(aimState, ndc, options),
      resetSight: () => assistedSight.reset(),
      cinematicFrame(kind, age, reduced, sector = 0, variant = null) {
        return briefings.frame(kind, age, reduced, sector, pilot.performanceDurations, variant);
      },
    };
  } catch (error) {
    dispose();
    throw error;
  }
}
